/**
 * The English dictionary, and the source of truth for what keys exist.
 *
 * Every other locale is typed as `Record<TranslationKey, string>`, so a missing
 * or misspelled key there is a compile error rather than an English word
 * surfacing mid-sentence on someone's screen.
 *
 * Counted phrases carry both forms rather than a plural rule: two locales with
 * the same simple one-or-many split do not justify a plural engine.
 */
export const en = {
  // Common
  "common.loading": "Loading",
  "common.tryAgain": "Try again",
  "common.cancel": "Cancel",
  "common.language": "Language",
  "common.english": "English",
  "common.french": "Français",

  // Errors
  "error.generic": "Something went wrong. Please try again.",
  "error.crashTitle": "Something went wrong",
  "error.crashBody": "An unexpected error occurred.",
  "error.notFoundTitle": "Page not found",
  "error.notFoundBody": "That page does not exist.",
  "error.notFoundHome": "Go home",
  "error.notFoundDetail": "This page does not exist, or the game link is no longer valid.",

  // Auth
  "auth.signIn": "Sign in",
  "auth.signingIn": "Signing in...",
  "auth.email": "Email address",
  "auth.password": "Password",
  "auth.noAccount": "No account?",
  "auth.createOne": "Create one",
  "auth.continueWith": "or continue with",
  "auth.signInFailed": "Sign-in failed. Please try again.",
  "auth.registrationClosed":
    "That account is not registered here, and this HootApp is not open for sign-ups. Ask the admin for an invite.",
  "auth.emailUnverified":
    "Your provider did not give us a verified email address, so we could not create an account.",
  "auth.oauthFailed": "Sign-in with that provider did not complete. Please try again.",

  // Join
  "join.nameTaken": "That name is already taken. Please choose a different one.",
  "join.notAccepting": "This game is not accepting new players right now.",
  "join.suggestFailed": "Could not suggest a name. Try typing one.",
  "join.enterName": "Please enter your name.",
  "join.nameTooLong": "Name must be 24 characters or less.",
  "join.twoStepIncomplete": "Tap all three colours from the host screen.",

  // Player game
  "play.yourScore": "Your score",
  "play.getReady": "Get ready...",
  "play.getReadyToAnswer": "Get ready to answer...",
  "play.answerSubmitted": "Answer submitted. Waiting for results...",
  "play.selectAll": "Select all correct answers, then submit.",
  "play.typeAnswer": "Type your answer",
  "play.submitAnswer": "Submit Answer",
  "play.slideContinue": "Continue on the host screen.",
  "play.correct": "Correct!",
  "play.incorrect": "Incorrect",
  "play.timesUp": "Time's up",
  "play.slide": "Slide",
  "play.noAnswerSubmitted": "No answer submitted.",
  "play.nothingToAnswer": "Nothing to answer here.",
  "play.totalScore": "Total score: {score} pts",
  "play.pointsEarned": "Points earned: {points}",
  "play.streak": "{count} in a row",
  "play.streakBonus": "(+{bonus} bonus)",
  "play.leaderboard": "Leaderboard",
  "play.yourScoreIs": "Your score: {score}",
  "play.gameOver": "Game over",
  "play.thanksForPlaying": "Thanks for playing!",
  "play.finalResults": "Final Results",
  "play.finalLeaderboard": "Final leaderboard",
  "play.connectionLost": "Connection lost — reconnecting...",
  "play.answerOptions": "Answer options",
  "play.scorePill": "{score} pts",
  "play.questionImage": "Question image: {prompt}",

  // Host game
  "hostGame.questionOf": "Question {current} of {total}",
  "hostGame.questionOfUnknown": "Question {current}",
  "hostGame.answeredCount": "{answered} of {total} answered",
  "hostGame.secondsLeft": "{seconds}s left",
  "hostGame.timerCloses": "Timer closes answers automatically",
  "hostGame.timerStarts": "Timer starts automatically",
  "hostGame.spaceToAdvance": "Space or Enter to advance",
  "hostGame.continue": "Continue",
  "hostGame.showLeaderboard": "Show Leaderboard",
  "hostGame.nextQuestion": "Next Question",
  "hostGame.showFinalResults": "Show Final Results",
  "hostGame.endSession": "End Session",
  "hostGame.waitingToStart": "Waiting for game to start...",
  "hostGame.sessionComplete": "Session complete",
  "hostGame.greatGame": "Great game!",
  "hostGame.correct": "Correct",
  "hostGame.playersWillType": "Players will type their answer.",
  "hostGame.accepted": "Accepted: {answers}",
  "hostGame.noAnswersSubmitted": "No answers submitted.",
  "hostGame.answerCount": " answers",
  "hostGame.oneAnswer": " answer",
  "hostGame.answersWithCount": "{count} answers",
  "hostGame.oneAnswerWithCount": "1 answer",
  "hostGame.blankAnswer": "(blank)",
  "hostGame.moreAnswersHidden":
    "{count} more answers were given once or twice and are not shown.",
  "hostGame.oneMoreAnswerHidden":
    "1 more answer was given once or twice and is not shown.",
  "hostGame.didntAnswer": "Didn't answer",
  "hostGame.playersWhoDidntAnswer": "Players who did not answer",
  "hostGame.playersWhoChose": "Players who chose {label}",
  "hostGame.playersWhoTyped": "Players who typed {text}",
  "hostGame.andMore": "+{count} more",
  "hostGame.answerDistribution": "Answer distribution",
  "hostGame.typedAnswerResults": "Typed answer results",
  "hostGame.morePlayersHidden": "{count} more players not shown",
  "hostGame.oneMorePlayerHidden": "1 more player not shown",
  "hostGame.topThree": "Top three players",
  "hostGame.remainingPlayers": "Remaining players",
  "hostGame.finalResults": "Final Results",
  "hostGame.leaderboard": "Leaderboard",
  "hostGame.continueReady": "Continue when everyone is ready.",
  "hostGame.answeredProgress": "{answered} of {total} answered",
  "hostGame.phaseQuestion": "Question",
  "hostGame.phaseReveal": "Answer reveal",
  "hostGame.phaseLeaderboard": "Leaderboard",
  "hostGame.phasePodium": "Final results",
  "hostGame.phaseEnded": "Session complete",
  "hostGame.phaseAborted": "Session ended",
  "hostGame.firstPlace": "1st",
  "hostGame.secondPlace": "2nd",
  "hostGame.thirdPlace": "3rd",
  "hostGame.connectionLost": "Connection lost — reconnecting...",

  // Quiz shelf
  "quiz.yourQuizzes": "Your quizzes",
  "quiz.shelfIntro": "Build, edit, and publish quizzes for your next family game night.",
  "quiz.newQuiz": "New quiz",
  "quiz.creating": "Creating...",
  "quiz.importJson": "Import JSON",
  "quiz.noQuizzesTitle": "No quizzes yet",
  "quiz.noQuizzesBody":
    "Your quiz shelf is empty. Start one and add questions to get a game ready.",
  "quiz.createFirst": "Create your first quiz",
  "quiz.loadFailed": "Could not load your quizzes.",
  "quiz.createFailed": "Could not create a new quiz.",
  "quiz.loadingQuizzes": "Loading quizzes",
  "quiz.questionCount": "{count} questions",
  "quiz.oneQuestion": "1 question",
  "quiz.draft": "draft",
  "quiz.published": "published",
  "quiz.untitled": "Untitled Quiz",

  // Import
  "import.title": "Import from JSON",
  "import.intro":
    "Paste a quiz file or choose one. It arrives as a draft, so you can look it over before publishing.",
  "import.chooseFile": "Choose a file",
  "import.pasteExample": "Paste an example",
  "import.jsonLabel": "Quiz JSON",
  "import.importQuiz": "Import quiz",
  "import.importing": "Importing...",
  "import.notJson": "That is not valid JSON.",
  "import.readFailed": "Could not read that file.",
  "import.failed": "Could not import that quiz.",
  "import.questionLabel": "Question {number}",
  "import.fileLabel": "File",
  "import.panelLabel": "Import a quiz from JSON",
  "join.enterCode": "Enter the 6-digit game code",
  "join.codeAriaLabel": "6-digit game code",
  "join.formLabel": "Enter game code",
  "join.codeRequired": "Please enter the 6-digit code shown on screen.",
  "join.gameNotFound": "Game not found. Check the code and try again.",
  "join.checking": "Checking...",
  "join.joinGame": "Join Game",
  "wait.removedTitle": "You were removed from this game.",
  "wait.removedBody": "You can join another game with a new code.",
  "wait.joinedAs": "You joined as",
  "wait.waitingForHost": "Waiting for the host to start",
  "notFound.joinGame": "Join a game",
  "common.loadingEllipsis": "Loading...",
  "common.secondsLeft": "{seconds} seconds left",
  "common.red": "red",
  "common.blue": "blue",
  "common.yellow": "yellow",
  "common.green": "green",
  "editor.loadingQuiz": "Loading quiz",

  // Landing and navigation
  "landing.siteNavigation": "Site navigation",
  "landing.home": "HootApp home",
  "landing.kicker": "Live quiz games",
  "landing.title": "Quiz night, anywhere.",
  "landing.tagline": "Join from your phone in seconds. No app install, no setup.",
  "landing.joinTitle": "Join a Game",
  "landing.joinSubtitle": "Enter the code shown on the host screen.",
  "landing.gameCode": "Game code",
  "landing.join": "Join",
  "landing.createQuiz": "Create a Quiz",
  "landing.createQuizSubtitle": "Build and host a live quiz game.",
  "landing.signInToCreate": "Sign in to create",
  "landing.howItWorks": "How it works",
  "landing.how1": "Host shares a 6-digit code.",
  "landing.how2": "Players join from any phone.",
  "landing.how3": "Answers and scores update live.",
  "landing.footer": "HootApp private family quiz game",

  // Auth forms
  "auth.registrationDisabled": "Registration is currently disabled.",
  "auth.enterEmail": "Enter your email address.",
  "auth.invalidEmail": "Enter a valid email address.",
  "auth.displayName": "Display name",
  "auth.enterDisplayName": "Enter your display name.",
  "auth.passwordMin": "Use at least 8 characters for your password.",
  "auth.inviteCode": "Invite code",
  "auth.enterInviteCode": "Enter your invite code.",
  "auth.checkForm": "Check the form details and try again.",
  "auth.registrationFailed": "Registration failed. Please try again.",
  "auth.accountExists": "An account with this email already exists. Sign in instead.",
  "auth.inviteInvalid": "That invite code does not look right. Check it and try again.",
  "auth.serverRegistrationFailed": "We could not create your account because the server hit a problem. Please try again.",
  "auth.alreadyAccount": "Already have an account?",
  "auth.creatingAccount": "Creating account...",
  "auth.createAccount": "Create account",
  "auth.continueGoogle": "Continue with Google",
  "auth.continueDiscord": "Continue with Discord",

  // Join and lobby
  "join.chooseName": "Choose your name",
  "join.yourName": "Your name",
  "join.yourNameHere": "Your name here",
  "join.suggestName": "Suggest",
  "join.suggestingName": "...",
  "join.maximumName": "Maximum 24 characters",
  "join.enterPattern": "Enter the color pattern from the host screen.",
  "join.selectedPattern": "Selected two-step pattern",
  "join.colorChoices": "Two-step color choices",
  "join.clearPattern": "Clear Pattern",
  "join.joining": "Joining...",
  "join.patternRequired": "Enter the 3-color pattern shown on the host screen.",
  "wait.playerCount": "{count} player{suffix} in the game",
  "hostLobby.waitingForPlayers": "Waiting for players",
  "hostLobby.connected": "Connected",
  "hostLobby.connecting": "Connecting...",
  "hostLobby.gameCode": "Game Code",
  "hostLobby.share": "Share",
  "hostLobby.copyLink": "Copy join link",
  "hostLobby.linkCopied": "Link copied",
  "hostLobby.copyFailed": "Copy failed - copy it manually",
  "hostLobby.qrLabel": "QR code linking to {url}",
  "hostLobby.scanToJoin": "Scan to join",
  "hostLobby.players": "Players ({count})",
  "hostLobby.playersInLobby": "Players in lobby",
  "hostLobby.noPlayers": "No players yet. Share the game code above.",
  "hostLobby.removePlayer": "Remove {nickname} from the game",
  "hostLobby.remove": "Remove",
  "hostLobby.settings": "Settings",
  "hostLobby.twoStepJoin": "Two-step join",
  "hostLobby.pattern": "Pattern",
  "hostLobby.randomQuestionOrder": "Random question order",
  "hostLobby.randomAnswerOrder": "Random answer order",
  "hostLobby.showLeaderboard": "Show leaderboard",
  "hostLobby.controls": "Lobby controls",
  "hostLobby.unlock": "Unlock Lobby",
  "hostLobby.lock": "Lock Lobby",
  "hostLobby.start": "Start Game",

  // Quiz editor
  "quiz.backToQuizzes": "Back to quizzes",
  "quiz.allQuizzes": "All quizzes",
  "quiz.title": "Quiz title",
  "quiz.save": "Save",
  "quiz.saving": "Saving...",
  "quiz.titleSaved": "Title saved.",
  "quiz.host": "Host",
  "quiz.hosting": "Starting...",
  "quiz.hostThisQuiz": "Host this quiz",
  "quiz.working": "Working...",
  "quiz.unpublish": "Unpublish",
  "quiz.publish": "Publish quiz",
  "quiz.questions": "Questions",
  "quiz.addQuestion": "Add question",
  "quiz.noQuestions": "No questions yet",
  "quiz.firstQuestion": "Add your first question to bring this quiz to life.",
  "quiz.questionType": "Question type",
  "quiz.typeSingle": "Single answer",
  "quiz.typeMultiple": "Multiple answers",
  "quiz.typeTrueFalse": "True or false",
  "quiz.typeAnswer": "Type answer",
  "quiz.typeSlide": "Slide",
  "quiz.true": "True",
  "quiz.false": "False",
  "quiz.editQuestion": "Edit question",
  "quiz.newQuestion": "New question",
  "quiz.addQuestionHeading": "Add a question",
  "quiz.slideText": "Slide text",
  "quiz.prompt": "Prompt",
  "quiz.slidePlaceholder": "What should appear on this slide?",
  "quiz.promptPlaceholder": "What do you want to ask?",
  "quiz.timerSeconds": "Timer (seconds)",
  "quiz.image": "Image",
  "quiz.imageForQuestion": "Image for question {number}: {prompt}",
  "quiz.answerOptions": "Answer options",
  "quiz.chooseEveryCorrect": "Choose every correct answer.",
  "quiz.chooseOneCorrect": "Choose the radio button next to the correct answer.",
  "quiz.markCorrect": "Mark option {number} correct",
  "quiz.optionLabel": "Option {number} label",
  "quiz.optionPlaceholder": "Option {number}",
  "quiz.removeOption": "Remove option {number}",
  "quiz.remove": "Remove",
  "quiz.addOption": "Add option",
  "quiz.acceptedAnswers": "Accepted answers",
  "quiz.acceptedAnswersPlaceholder": "One accepted answer per line",
  "quiz.saveChanges": "Save changes",
  "quiz.saveQuestion": "Save question",
  "quiz.dragReorder": "Drag to reorder",
  "quiz.dragQuestion": "Drag to reorder question {number}",
  "quiz.moveQuestionUp": "Move question {number} up",
  "quiz.moveQuestionDown": "Move question {number} down",
  "quiz.editQuestionNumber": "Edit question {number}",
  "quiz.deleteQuestionNumber": "Delete question {number}",
  "quiz.deleteQuestionConfirm": "Delete question {number}?",
  "quiz.correctAnswer": "Correct answer: {answer}",
  "quiz.acceptedAnswersSummary": "Accepted answers: {answers}",
  "quiz.enterSlideText": "Enter slide text.",
  "quiz.enterPrompt": "Enter a question prompt.",
  "quiz.timerRange": "Timer must be between {min} and {max} seconds.",
  "quiz.optionNeedsLabel": "Every answer option needs a label.",
  "quiz.markOneCorrect": "Mark at least one option as correct.",
  "quiz.markExactlyOne": "Mark exactly one option as correct.",
  "quiz.addAcceptedAnswer": "Add at least one accepted answer.",
  "quiz.loadThisFailed": "Could not load this quiz.",
  "quiz.reorderFailed": "Could not reorder questions.",
  "quiz.titleRequired": "The quiz needs a title.",
  "quiz.saveTitleFailed": "Could not save the title.",
  "quiz.publishStatusFailed": "Could not update the publish status.",
  "quiz.startSessionFailed": "Could not start a session.",
  "quiz.addQuestionFailed": "Could not add the question.",
  "quiz.deleteQuestionFailed": "Could not delete the question.",
  "quiz.saveQuestionFailed": "Could not save the question.",

  // Reports and shared admin labels
  "admin.breadcrumb": "Breadcrumb",
  "admin.quizzes": "Quizzes",
  "admin.edit": "Edit",
  "admin.reports": "Reports",
  "admin.detail": "Detail",
  "reports.loading": "Loading reports...",
  "reports.title": "Reports",
  "reports.empty": "No completed sessions yet.",
  "reports.sessionReports": "Session reports",
  "reports.quiz": "Quiz",
  "reports.ended": "Ended",
  "reports.players": "Players",
  "reports.questions": "Questions",
  "reports.status": "Status",
  "reports.actions": "Actions",
  "reports.view": "View",
  "reports.downloadCsvFor": "Download CSV for {quiz}",
  "reports.loadingReport": "Loading report...",
  "reports.notFound": "Report not found.",
  "reports.endedAt": "Ended: {date}",
  "reports.downloadCsv": "Download CSV",
  "reports.downloadCsvExport": "Download CSV export",
  "reports.sessionSummary": "Session summary",
  "reports.participants": "Participants",
  "reports.participantRankings": "Participant rankings",
  "reports.rank": "Rank",
  "reports.nickname": "Nickname",
  "reports.score": "Score",
  "reports.correct": "Correct",
  "reports.accuracy": "Accuracy",
  "reports.questionAnalytics": "Question Analytics",
  "reports.question": "Question",
  "reports.totalAnswers": "Total Answers",
  "reports.noPrompt": "(no prompt)",
} as const;

export type TranslationKey = keyof typeof en;
